import { usePathname } from "next/navigation";
import { useState } from "react";
import { ExpandedState, NavItemKind, NavItemProps } from "./NavItem";

const decodeExpanded = (
    expandedState: ExpandedState,
    defaultVal: () => boolean,
) => {
    switch (expandedState) {
        case "Expanded":
            return true;
        case "Collapsed":
            return false;
        case "Default":
            return defaultVal();
    }
};

// helper function to recursively find selected sub nav item
const containsSelectedItem = (items: NavItemProps[], slug: String): boolean => {
    if (!items.length) {
        return false;
    }

    for (const item of items) {
        if (item.url === slug) {
            return true;
        }

        if (
            item.subItems?.length &&
            containsSelectedItem(item.subItems, slug)
        ) {
            return true;
        }
    }
    return false;
};

const isSelected = (
    subItems: NavItemProps[],
    currentSlug: string,
    url: string,
) => {
    // To avoid the need for redirects some parents items are actually just links to their single child
    // they should not ALSO be selected when the child is selected
    return (
        currentSlug === url &&
        (subItems.length === 0 || !containsSelectedItem(subItems, currentSlug))
    );
};

const NONE = -1;
const DEFAULT = -2;
export const useNavItem = (
    url: string,
    kind: NavItemKind,
    title: string,
    subItems: NavItemProps[],
    expandedState: ExpandedState,
    accessabilityAppend?: string,
) => {
    const currentSlug = usePathname();
    const isCurrentSelected = isSelected(subItems, currentSlug, url);

    const defaultExpanded = decodeExpanded(
        expandedState,
        () =>
            (isCurrentSelected && !!subItems.length) ||
            containsSelectedItem(subItems, currentSlug),
    );

    const alwaysExpanded = kind === "page" && subItems.length;

    const isCollapsible = subItems.length && !alwaysExpanded;

    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const [expandedChild, setExpandedChild] = useState(DEFAULT);

    const parentAccessibleTitle = (() => {
        switch (kind) {
            case "main":
                return [];
            case "sub-section":
                return [title];
            case "page":
            case "sub-page":
                return [title, accessabilityAppend || []];
        }
    })()
        .flat()
        .join(" ");
    const updatedSubItems: NavItemProps[] =
        kind == "main"
            ? subItems.map((item, index) => {
                  return {
                      ...item,
                      onToggle: (expanding: boolean) => {
                          if (!expanding) {
                              setExpandedChild(NONE);
                          } else {
                              setExpandedChild(index);
                          }
                      },
                      expandedState:
                          expandedChild === DEFAULT
                              ? "Default"
                              : index === expandedChild
                              ? "Expanded"
                              : "Collapsed",
                  };
              })
            : subItems.map((item) => {
                  return {
                      ...item,
                      accessabilityAppend: parentAccessibleTitle,
                  };
              });

    return {
        isSelected: isCurrentSelected,
        alwaysExpanded: alwaysExpanded,
        isCollapsible: isCollapsible,
        defaultExpanded: defaultExpanded,
        isExpanded:
            expandedState !== "Default"
                ? expandedState === "Expanded"
                : isExpanded,
        setIsExpanded: setIsExpanded,
        subItems: updatedSubItems,
    };
};
